/**
 * วิเคราะห์ค่าจ้าง (Variance Bridge, A13) — OWNER only. Explains why total net
 * payroll moved vs the prior period, decomposed into drivers. All numbers come
 * rule-computed from the API; the LLM only narrates (decision #6).
 */
import { useState } from 'react';
import { TrendingUp, TrendingDown, Sparkles, AlertTriangle } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Loading } from '../../components/layout/Loading';
import { LoadError } from '../../components/layout/LoadError';
import { usePayrollAnalysis, type BridgeDriver } from '../../hooks';

const fmt = (n: number) =>
  new Intl.NumberFormat('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
const signed = (n: number) => `${n >= 0 ? '+' : '−'}${fmt(Math.abs(n))}`;

function DriverBar({ d, max }: { d: BridgeDriver; max: number }) {
  const up = d.amount >= 0;
  const width = max > 0 ? Math.max((Math.abs(d.amount) / max) * 100, 2) : 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="text-sm text-gray-700 w-40 shrink-0 truncate">{d.label}</span>
      <div className="flex-1 h-2 bg-gray-100 rounded">
        <div className={`h-2 rounded ${up ? 'bg-brand-500' : 'bg-red-400'}`} style={{ width: `${width}%` }} />
      </div>
      <span className={`text-sm font-semibold tabular-nums w-24 text-right ${up ? 'text-brand-700' : 'text-red-600'}`}>
        {signed(d.amount)}
      </span>
    </div>
  );
}

/** Content-only version used by the Wage hub. */
export function PayrollAnalysisContent() {
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const { data, isLoading, error, refetch } = usePayrollAnalysis(period);

  return (
    <>
      <div className="space-y-5 max-w-2xl">
        <input
          type="month"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
        />

        {error ? (
          <LoadError error={error} onRetry={() => refetch()} />
        ) : isLoading || !data ? (
          <Loading />
        ) : data.bridge.curHeadcount === 0 ? (
          <div className="card p-4 text-sm text-gray-500">ยังไม่มีข้อมูลเงินเดือนของงวดนี้</div>
        ) : (
          <>
            {/* Headline */}
            <div className="card p-4">
              <div className="flex items-center gap-3">
                {data.bridge.deltaNet >= 0 ? (
                  <TrendingUp className="w-8 h-8 text-brand-600" />
                ) : (
                  <TrendingDown className="w-8 h-8 text-red-500" />
                )}
                <div>
                  <p className="text-2xl font-bold tabular-nums">
                    {data.bridge.deltaPct !== null ? `${signed(Math.round(data.bridge.deltaPct * 100))}%` : signed(data.bridge.deltaNet)}
                  </p>
                  <p className="text-sm text-gray-500">
                    เทียบงวด {data.bridge.priorPeriod} · {signed(data.bridge.deltaNet)} บาท
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div>
                  <p className="text-xs text-gray-500">ค่าจ้างสุทธิงวดนี้</p>
                  <p className="font-semibold tabular-nums">{fmt(data.bridge.curNet)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">พนักงาน</p>
                  <p className="font-semibold tabular-nums">
                    {data.bridge.curHeadcount}
                    {data.bridge.curHeadcount !== data.bridge.priorHeadcount && (
                      <span className="text-gray-400"> (เดิม {data.bridge.priorHeadcount})</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">OT / ต่อหัว</p>
                  <p className="font-semibold tabular-nums">
                    {Math.round(data.bridge.otRatio * 100)}% · {fmt(data.bridge.costPerHead)}
                  </p>
                </div>
              </div>
            </div>

            {/* Narration */}
            {data.summary && (
              <div className="card p-4 border-l-4 border-l-brand-400 bg-brand-50/40">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.summary}</p>
                <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> เรียบเรียงด้วย AI
                </p>
              </div>
            )}

            {/* Variance bridge */}
            <div className="card p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">ที่มาของการเปลี่ยนแปลง</h3>
              {data.bridge.drivers.map((d) => (
                <DriverBar key={d.key} d={d} max={Math.max(...data.bridge.drivers.map((x) => Math.abs(x.amount)), 1)} />
              ))}
              {!data.bridge.reconciled && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> ยอดรวมไม่ลงตัวพอดี — มีรายการที่อธิบายไม่ได้
                </p>
              )}
            </div>

            {/* Outliers */}
            {data.bridge.outliers.length > 0 && (
              <div className="card p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">พนักงานที่เปลี่ยนแปลงมากสุด</h3>
                <div className="space-y-1">
                  {data.bridge.outliers.map((o) => (
                    <div key={o.emp_code} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{o.emp_code}</span>
                      <span className={`font-semibold tabular-nums ${o.delta >= 0 ? 'text-brand-700' : 'text-red-600'}`}>
                        {signed(o.delta)}
                        {o.pct !== null && <span className="text-gray-400 font-normal"> ({signed(Math.round(o.pct * 100))}%)</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

/** Standalone page kept for legacy /owner/payroll-analysis (now redirects via Wage hub). */
export function PayrollAnalysis() {
  return (
    <AdminLayout title="วิเคราะห์ค่าจ้าง">
      <PayrollAnalysisContent />
    </AdminLayout>
  );
}
